import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  BarChart as StatsIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { promotionsService } from '../services/api';
// Fonction utilitaire pour formater les dates
const formatDate = (dateString) => {
  if (!dateString) return 'Indéfini';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).format(date);
};

const Promotions = () => {
  const navigate = useNavigate();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await promotionsService.getAll();
      setPromotions(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des promotions:', err);
      setError('Impossible de charger les promotions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette promotion ?')) {
      try {
        await promotionsService.delete(id);
        setDeleteSuccess(true);
        fetchPromotions();
        setTimeout(() => setDeleteSuccess(false), 3000);
      } catch (err) {
        console.error('Erreur lors de la suppression:', err);
        setError('Impossible de supprimer la promotion');
      }
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
  };

  const getTypeLabel = (type) => {
    const types = {
      percentage: 'Pourcentage',
      fixed: 'Montant fixe',
      buy_x_get_y: 'X achetés, Y offerts',
      free_shipping: 'Livraison gratuite',
    };
    return types[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      percentage: 'primary',
      fixed: 'success',
      buy_x_get_y: 'warning',
      free_shipping: 'info',
    };
    return colors[type] || 'default';
  };

  const getTargetLabel = (targetType) => {
    const targets = {
      all: 'Tous les produits',
      product: 'Produit(s) spécifique(s)',
      brand: 'Marque(s)',
      family: 'Famille(s)',
      category: 'Catégorie(s)',
      subcategory: 'Sous-catégorie(s)',
    };
    return targets[targetType] || targetType;
  };


  const filteredPromotions = promotions.filter((promotion) =>
    promotion.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (promotion.code && promotion.code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Promotions
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/promotions/new')}
        >
          Nouvelle promotion
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {deleteSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDeleteSuccess(false)}>
          Promotion supprimée avec succès
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Rechercher par nom ou code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Réduction</TableCell>
              <TableCell>Ciblage</TableCell>
              <TableCell>Utilisation</TableCell>
              <TableCell>Dates</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPromotions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    Aucune promotion trouvée
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredPromotions.map((promotion) => (
                <TableRow key={promotion.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {promotion.name}
                    </Typography>
                    {promotion.autoApply && (
                      <Chip label="Auto" size="small" color="info" sx={{ mt: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {promotion.code ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={promotion.code}
                          size="small"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace' }}
                        />
                        <IconButton size="small" onClick={() => handleCopyCode(promotion.code)}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getTypeLabel(promotion.type)}
                      size="small"
                      color={getTypeColor(promotion.type)}
                    />
                  </TableCell>
                  <TableCell>
                    {promotion.type === 'percentage' && `${promotion.discountValue}%`}
                    {promotion.type === 'fixed' && `${promotion.discountValue}€`}
                    {promotion.type === 'buy_x_get_y' &&
                      `${promotion.buyQuantity} achetés, ${promotion.getQuantity} offert(s)`}
                    {promotion.type === 'free_shipping' && 'Livraison offerte'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getTargetLabel(promotion.targetType)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {promotion.currentUses}
                      {promotion.maxUses && ` / ${promotion.maxUses}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">
                      Du: {formatDate(promotion.startDate)}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Au: {formatDate(promotion.endDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={promotion.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={promotion.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Statistiques">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/promotions/${promotion.id}/stats`)}
                      >
                        <StatsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Modifier">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/promotions/edit/${promotion.id}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(promotion.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Promotions;

